import { AlertTriangle, TrendingUp, Clock, Zap, ArrowRight } from "lucide-react";

export function FearSection({ t }) {
  return (
    <section className="py-24 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)] relative overflow-hidden" data-testid="fear-section">
      {/* Background glow */}
      <div className="absolute inset-0 opacity-5" style={{background: 'radial-gradient(ellipse at center, #a855f7 0%, transparent 70%)'}} />

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Main fear headline */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-900/20 border border-purple-900/40 mb-6">
            <AlertTriangle className="w-4 h-4 text-[#a855f7]" />
            <span className="text-[#a855f7] font-['JetBrains_Mono'] text-xs uppercase tracking-wider" data-testid="fear-warning-tag">
              {t.fear_warning || "WARNING"}
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-6">
            {t.fear_h1 || "AI recommends brands"}<br />
            {t.fear_h1_2 || "that rank"} <span className="text-[#a855f7]">{t.fear_h1_highlight || "TODAY"}</span>
          </h2>
          <p className="text-[var(--color-text-muted)] text-lg max-w-3xl mx-auto leading-relaxed">
            {t.fear_desc || "ChatGPT, Gemini and Perplexity pull answers from top-ranking pages. If you're not ranking now, AI will recommend your competitors — and it only gets harder to catch up."}
          </p>
        </div>

        {/* Timeline */}
        <div className="grid md:grid-cols-3 gap-6 mb-16" data-testid="fear-timeline">
          {[
            {
              month: t.fear_m1_label || "Month 1",
              title: t.fear_m1_title || "Your competitor publishes content",
              desc: t.fear_m1_desc || "While you wait, they're building SEO authority and getting cited by AI engines.",
              color: "text-yellow-400", border: "border-yellow-900/40", bg: "bg-yellow-900/10"
            },
            {
              month: t.fear_m3_label || "Month 3",
              title: t.fear_m3_title || "AI starts recommending them",
              desc: t.fear_m3_desc || "ChatGPT and Perplexity now cite your competitors by default. Your traffic drops.",
              color: "text-orange-400", border: "border-orange-900/40", bg: "bg-orange-900/10"
            },
            {
              month: t.fear_m6_label || "Month 6",
              title: t.fear_m6_title || "You're invisible. They own your niche.",
              desc: t.fear_m6_desc || "AI models reinforce what already works. Catching up now costs 10x more effort and money.",
              color: "text-[#a855f7]", border: "border-purple-900/40", bg: "bg-red-900/10"
            },
          ].map((item, i) => (
            <div key={i} className={`${item.bg} border ${item.border} p-6 relative`} data-testid={`fear-card-${i}`}>
              <div className={`text-xs font-['JetBrains_Mono'] uppercase tracking-wider ${item.color} mb-3`}>
                {item.month}
              </div>
              <h3 className="text-white font-bold font-['Cabinet_Grotesk'] text-lg mb-2">{item.title}</h3>
              <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">{item.desc}</p>
              {i < 2 && <ArrowRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[var(--color-text-muted)] z-10" />}
            </div>
          ))}
        </div>

        {/* Urgency CTA */}
        <div className="text-center bg-[var(--color-surface)] border-2 border-[#a855f7] p-8 md:p-12" data-testid="fear-cta-box">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Clock className="w-5 h-5 text-[#a855f7]" />
            <span className="text-[#a855f7] font-['JetBrains_Mono'] text-sm uppercase tracking-wider">
              {t.fear_urgency || "Every day you wait, you lose customers"}
            </span>
          </div>
          <h3 className="text-3xl sm:text-4xl font-black font-['Cabinet_Grotesk'] tracking-tighter mb-4">
            {t.fear_cta_h || "Start ranking before your competitors do"}
          </h3>
          <p className="text-[var(--color-text-muted)] mb-8 max-w-xl mx-auto">
            {t.fear_cta_sub || "The brands ranking today become the default AI recommendations tomorrow. Make sure that's you."}
          </p>
          <a href="#pricing" className="inline-flex items-center gap-3 px-10 py-4 bg-[#a855f7] text-white font-black text-lg uppercase tracking-wider hover:bg-purple-600 transition-all" data-testid="fear-cta-btn">
            <Zap className="w-5 h-5" />
            {t.fear_cta_btn || "GET STARTED NOW"}
          </a>
        </div>
      </div>
    </section>
  );
}
